/**
 * `/` — the globe is the hero (spec §5). A quiet line of live numbers, the
 * name, one sentence, two actions. In the native app this page also runs the
 * first-launch sequence (spec §2.5): rotate, ask for location after ~2 s,
 * descend to the user's cell and hand off to the map; on deny keep browsing.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { pixelFromLatLng, tileCenterLatLng } from '@drawearth/geo';
import type { SiteStats } from '@drawearth/shared';
import { Globe } from '../components/Globe.tsx';
import { Icon, InkGauge, ThemeToggle, TopBar } from '../components/Widgets.tsx';
import { LOCATION_PURPOSE, REPLAY_ENABLED } from '../config.ts';
import { api } from '../lib/api.ts';
import { drawingAvailable, isNative, requestLocationPermission, watchPosition } from '../lib/native.ts';
import { startPositionWatch } from '../lib/session.ts';
import { appStore, useApp } from '../lib/store.ts';

const INTRO_KEY = 'drawearth.introSeen';

type Stage = 'rotating' | 'asking' | 'descending' | 'denied' | 'done';

function ago(ts: number): string {
  const m = Math.round((Date.now() - ts) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function Home() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const user = useApp((s) => s.user);
  const authReady = useApp((s) => s.authReady);
  const replay = search.get('intro') === '1';
  const runIntro = drawingAvailable() && (replay || localStorage.getItem(INTRO_KEY) !== '1');
  const [stage, setStage] = useState<Stage>(runIntro ? 'rotating' : 'done');
  const [target, setTarget] = useState<{ lat: number; lng: number } | null>(null);
  const [stats, setStats] = useState<SiteStats | null>(null);
  const targetPos = useRef<{ lat: number; lng: number } | null>(null);
  const viewed = useRef(new Set<string>());

  useEffect(() => {
    let alive = true;
    const load = (): void => {
      void api
        .stats()
        .then((s) => alive && setStats(s))
        .catch(() => undefined);
    };
    load();
    const id = setInterval(load, 20_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!runIntro) return;
    let cancelled = false;
    let stop: (() => void) | null = null;
    const timer = setTimeout(async () => {
      setStage('asking');
      const perm = await requestLocationPermission();
      if (cancelled) return;
      if (perm !== 'granted') {
        setStage('denied');
        return;
      }
      setStage('descending');
      // Network location first, refined when GPS lands (spec §2.5 step 3).
      stop = await watchPosition(
        (p) => {
          const centre = tileCenterLatLng(pixelFromLatLng(p.lat, p.lng).tile);
          targetPos.current = { lat: p.lat, lng: p.lng };
          setTarget((t) => t ?? centre);
        },
        () => setStage('denied'),
      );
    }, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      stop?.();
    };
  }, [runIntro]);

  const finishIntro = (): void => {
    localStorage.setItem(INTRO_KEY, '1');
    if (user) void api.meta({ type: 'intro_seen' }).catch(() => undefined);
    void startPositionWatch();
    const p = targetPos.current ?? target;
    if (p) navigate(`/map?lat=${p.lat.toFixed(6)}&lng=${p.lng.toFixed(6)}&z=17`);
    else navigate('/map');
  };

  /** Zooming into the sphere (or tapping a painter) opens the map at that place. */
  const enterMap = (p: { lat: number; lng: number; zoom: number }): void => {
    localStorage.setItem(INTRO_KEY, '1');
    navigate(`/map?lat=${p.lat.toFixed(6)}&lng=${p.lng.toFixed(6)}&z=${p.zoom}`);
  };

  const onCellsViewed = (cells: string[]): void => {
    let added = false;
    for (const c of cells)
      if (!viewed.current.has(c)) {
        viewed.current.add(c);
        added = true;
      }
    if (added && appStore.get().user) void api.meta({ type: 'globe_view', cells }).catch(() => undefined);
  };

  const live = stats?.liveCells ?? null;
  const canPaint = drawingAvailable();
  const last = stats?.recent[0];

  return (
    <div className="page page--canvas">
      <TopBar right={<ThemeToggle />} />
      <Globe
        rpm={1}
        target={stage === 'descending' ? target : null}
        onArrive={finishIntro}
        onEnter={enterMap}
        onCellsViewed={onCellsViewed}
        className="globe--hero"
      />
      <div className="hero">
        {live !== null && (
          <span className="hero__live" data-testid="live">
            {live === 0 ? 'quiet right now' : `${live} ${live === 1 ? 'place' : 'places'} painting right now`}
          </span>
        )}
        <h1>Draw Earth</h1>
        <p className="hero__lead">A pixel canvas the size of the planet. Paint where you stand.</p>
        {stats && (
          <div className="hero__stats" data-testid="stats">
            <span>{stats.pixels.toLocaleString()} pixels</span>
            <span>{stats.places.toLocaleString()} places</span>
            <span>{stats.painters.toLocaleString()} painters</span>
            {last && (
              <Link to={`/t/${last.tile}`} data-testid="last-drawing">
                last drawing {ago(last.at)}
              </Link>
            )}
            {user && <InkGauge ink={user.ink} night />}
          </div>
        )}
        {stage === 'asking' && <p className="hero__note">{LOCATION_PURPOSE}</p>}
        {stage === 'descending' && <p className="hero__note">Finding your place…</p>}
        {stage === 'denied' && (
          <p className="hero__note" data-testid="location-denied">
            Turn on location to paint. Browsing works without it.{' '}
            <button
              type="button"
              className="btn btn--paper"
              onClick={() => {
                localStorage.setItem(INTRO_KEY, '1');
                navigate('/map');
              }}
            >
              Open the map
            </button>
          </p>
        )}
        {(stage === 'done' || stage === 'rotating') && (
          <div className="row hero__actions">
            {canPaint ? (
              <>
                <Link className="btn btn--big" to="/map" data-testid="cta-map">
                  <Icon name="brush" /> Paint where you are
                </Link>
                {REPLAY_ENABLED && (
                  <Link className="hero__alt" to="/map">
                    or replay a place
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link className="btn btn--big" to="/map" data-testid="cta-map">
                  <Icon name="map" /> Explore the map
                </Link>
                <a className="hero__alt" href="#get-the-app">
                  or get the app to paint
                </a>
              </>
            )}
            {isNative() && authReady && !user && (
              <Link className="btn btn--big btn--ghost" to="/signin">
                Sign in to paint
              </Link>
            )}
          </div>
        )}
        <p className="hero__footer" id="get-the-app">
          Painting needs the app for iPhone or Android. The web is for browsing. <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </p>
      </div>
    </div>
  );
}
